import React, { useState } from 'react';
import { Warning, TrendUp, CaretDown, CaretUp, FilePdf } from '@phosphor-icons/react';

const HEALTH = {
  green:    { label: 'Healthy',  bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500', border: 'border-emerald-300' },
  amber:    { label: 'Watch',    bg: 'bg-amber-100',   text: 'text-amber-700',   bar: 'bg-amber-500',   border: 'border-amber-300' },
  red:      { label: 'At Risk',  bg: 'bg-red-100',     text: 'text-red-700',     bar: 'bg-red-500',     border: 'border-red-300' },
  critical: { label: 'Critical', bg: 'bg-red-200',     text: 'text-red-800',     bar: 'bg-red-700',     border: 'border-red-500' },
  unknown:  { label: 'No Data',  bg: 'bg-zinc-100',    text: 'text-zinc-500',    bar: 'bg-zinc-300',    border: 'border-zinc-200' },
};

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

export const MarginBadge = ({ row }) => {
  if (!row) return null;
  const h = HEALTH[row.health] || HEALTH.unknown;
  if (row.health === 'unknown') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded ${h.bg} ${h.text}`}
        title="Add recipe or set a target cost to assess margin" data-testid={`margin-badge-${row.index}`}>
        No cost data
      </span>
    );
  }
  const est = row.cost_basis === 'estimated';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded ${h.bg} ${h.text} ${est ? 'border border-dashed ' + h.border : ''}`}
      title={`Cost ${fmt(row.total_cost_per_unit)}/unit ${est ? '(estimated)' : '(from PO history)'} · Margin ${row.margin_pct}%`} data-testid={`margin-badge-${row.index}`}>
      {row.health === 'critical' && <Warning size={11} weight="fill" />}
      {row.margin_pct}% margin
      {est && <span className="text-[8px] font-bold uppercase opacity-70" data-testid={`margin-est-${row.index}`}>· Est</span>}
    </span>
  );
};

export const MarginHealthMeter = ({ data, onExport }) => {
  const [open, setOpen] = useState(true);
  const [exporting, setExporting] = useState(false);
  if (!data || !data.project_margin) return null;
  const pm = data.project_margin;
  const h = HEALTH[pm.health] || HEALTH.unknown;
  const risk = data.risk_items || { critical: [], red: [], amber: [] };
  const riskList = [...(risk.critical || []), ...(risk.red || []), ...(risk.amber || [])];
  const pct = pm.weighted_margin_pct;
  const barWidth = pct === null ? 0 : Math.max(2, Math.min(100, pct));

  const handleExport = async () => {
    if (!onExport) return;
    setExporting(true);
    try { await onExport(); } finally { setExporting(false); }
  };

  return (
    <div className={`border ${h.border} bg-white mb-6`} data-testid="margin-health-meter">
      <div className={`flex items-center justify-between px-4 py-3 ${h.bg}`}>
        <div className="flex items-center gap-3">
          <TrendUp size={20} className={h.text} weight="bold" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Project Margin Health</div>
            <div className={`text-2xl font-bold ${h.text}`} data-testid="project-margin-pct">
              {pct === null ? '—' : `${pct}%`}
              <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded ${h.bg} ${h.text} border ${h.border}`}>{h.label}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-5 text-right">
          <div><div className="text-[10px] text-zinc-500 uppercase">Quoted</div><div className="text-sm font-bold text-zinc-800">{fmt(pm.total_quoted_value)}</div></div>
          <div><div className="text-[10px] text-zinc-500 uppercase">Est. Cost</div><div className="text-sm font-bold text-zinc-800">{fmt(pm.total_cost)}</div></div>
          <div><div className="text-[10px] text-zinc-500 uppercase">Margin</div><div className={`text-sm font-bold ${h.text}`}>{fmt(pm.total_margin)}</div></div>
          {onExport && (
            <button onClick={handleExport} disabled={exporting}
              className="flex items-center gap-1 text-xs font-semibold text-zinc-700 bg-white border border-zinc-300 px-3 py-1.5 rounded hover:bg-zinc-50 disabled:opacity-50"
              data-testid="export-risk-summary-btn">
              <FilePdf size={14} /> {exporting ? 'Exporting…' : 'Risk Summary PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Weighted bar */}
      <div className="px-4 pt-3">
        <div className="h-2 w-full bg-zinc-100 rounded overflow-hidden">
          <div className={`h-full ${h.bar} transition-all duration-500`} style={{ width: `${barWidth}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
          <span>0%</span><span>Green ≥20% · Amber 10–20% · Red &lt;10% · Critical &lt;5%</span><span>100%</span>
        </div>
      </div>

      <div className="px-4 py-2 flex items-center justify-between text-[11px] text-zinc-500">
        <span>{pm.assessed_count} item(s) assessed · {pm.unassessed_count} without cost data</span>
        {riskList.length > 0 && (
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1 font-semibold text-zinc-700 hover:text-zinc-900" data-testid="toggle-risk-panel">
            {riskList.length} flagged item(s) {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>
        )}
      </div>

      {open && riskList.length > 0 && (
        <div className="border-t border-zinc-100 px-4 py-3" data-testid="risk-concentration-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Risk Concentration — Red &amp; Amber Items</div>
            {onExport && (
              <button onClick={handleExport} disabled={exporting}
                className="flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded hover:bg-red-100 disabled:opacity-50"
                data-testid="export-risk-summary-panel-btn">
                <FilePdf size={12} /> Export
              </button>
            )}
          </div>
          <div className="space-y-1">
            {riskList.map(r => {
              const rh = HEALTH[r.health] || HEALTH.unknown;
              return (
                <div key={r.index} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded border ${rh.border} ${rh.bg}`} data-testid={`risk-item-${r.index}`}>
                  <span className="flex items-center gap-1.5 text-zinc-700 truncate max-w-[60%]">
                    {r.health === 'critical' && <Warning size={12} weight="fill" className="text-red-700" />}
                    #{r.index + 1} {r.description || 'Untitled'}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-zinc-500">Cost {fmt(r.total_cost_per_unit)}/u</span>
                    <span className={`font-bold ${rh.text}`}>{r.margin_pct}%</span>
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">Advisory only — the system flags risk, it never blocks. You decide.</p>
        </div>
      )}
    </div>
  );
};
